// The "Message copy" block: one textarea per lifecycle hook, with a button that
// fills in the registry default so it can be edited.
//
// Shared by the event form and the series form. It was duplicated in both,
// verbatim down to `hookLabel()`, which is exactly how the two drift.
//
// THE ONE IDEA WORTH UNDERSTANDING HERE. An empty box is not "unset", it is a
// live choice, and it means something different from a filled one:
//
//   empty  -> the registry default is sent, resolved at SEND time. Reword the
//             default in shared/src/events.ts later and this group gets the new
//             wording for free.
//   filled -> this exact text is stored on the event and sent forever, frozen
//             at whatever the default said the day the button was pressed.
//
// So "Edit this wording" is not a convenience that changes nothing — it turns a
// tracking reference into a snapshot. Hence the paired "Reset to default" that
// empties the box again, and the state line under each box naming which of the
// two is in force. Will asked for the autofill after reading the grey
// placeholder as a sample that was NOT being sent, so the copy change matters
// at least as much as the button.
//
// A box is only shown when its hook can actually FIRE. `min_met` needs an
// `rsvp_min` and `full` needs an `rsvp_max`, and with the threshold blank the
// server's `fireRsvpHooks()` skips the hook entirely — so an editable box there
// is configuration that looks live, saves fine, and can never be sent. The rule
// itself is `hookCanFire()` in @msc/shared, next to the registry it belongs to;
// this file only does the showing and hiding.
//
// Emoji are just characters in the string — there is no icon set and nothing to
// configure. Inserting a different one is the operating system's job (Windows:
// Win + . , macOS: Control+Command+Space, iOS: the emoji key on the keyboard),
// which the hint says out loud because "how would a user select different
// icons" was the actual question.

import type { LifecycleHook } from '@msc/shared';
import { el } from '../dom.js';

export interface MessageCopyFields {
  /** The whole block: heading, hint, and one field group per hook. */
  element: HTMLElement;
  /** Hook -> the organizer's non-empty override. Omits every empty box. */
  value(): Record<string, string>;
  /**
   * Show only the hooks that can actually fire right now.
   *
   * Call it whenever something a hook depends on changes — on these forms, the
   * minimum and maximum RSVP boxes. A hidden field KEEPS its value and keeps
   * submitting it: clearing the minimum must not silently destroy wording the
   * organizer typed, and putting the minimum back should bring it straight back.
   */
  setReachable(canFire: (hook: LifecycleHook) => boolean): void;
}

export interface MessageCopyFieldsOptions {
  /** `[hook, default copy]` in registry order — i.e. `Object.entries(def.hooks)`. */
  hooks: readonly (readonly [LifecycleHook, string])[];
  /** Existing overrides when editing. Absent means "use the default". */
  existing?: Partial<Record<LifecycleHook, string>> | null;
  /** Id prefix, so two of these on one page cannot collide. */
  idPrefix: string;
  /**
   * Emit `name="typeMessages[hook]"` on each textarea.
   *
   * The event form does; the series form never has. Both submit by script, so
   * it only matters to the no-JS fallback that the `method="post"` rule exists
   * for — kept as an option rather than switched on for both, because the
   * series endpoint has never accepted a urlencoded body and pretending
   * otherwise in the markup would be a lie.
   */
  named?: boolean;
  /** An extra line above the fields. The series form explains inheritance. */
  intro?: string;
}

function hookLabel(hook: LifecycleHook): string {
  if (hook === 'needs_min') return 'While the minimum is not reached yet';
  if (hook === 'min_met') return 'When the minimum is reached';
  if (hook === 'full') return 'When it fills up';
  return 'When it ends';
}

export function messageCopyFields(opts: MessageCopyFieldsOptions): MessageCopyFields {
  const { hooks, existing, idPrefix, named = false, intro } = opts;
  const boxes = new Map<LifecycleHook, HTMLTextAreaElement>();
  const fields = new Map<LifecycleHook, HTMLElement>();

  const children: HTMLElement[] = [el('h3', {}, 'Message copy')];
  if (intro) children.push(el('p', { class: 'muted small' }, intro));
  children.push(
    el(
      'p',
      { class: 'muted small' },
      'Every box below is already using the message shown in it. The first is shown on the ' +
        'event, the rest are posted to the group. Press a button to ' +
        'edit that wording instead. Placeholders: {title} {group} {min} {max} {count} ' +
        '{winners}. The icons are ordinary emoji. Replace one with your keyboard’s ' +
        'emoji picker (Windows: Win + period, Mac: Control+Command+Space).',
    ),
  );

  for (const [hook, fallback] of hooks) {
    const id = `${idPrefix}-msg-${hook}`;
    const attrs: Record<string, string | number> = { id, rows: 2, placeholder: fallback };
    if (named) attrs['name'] = `typeMessages[${hook}]`;
    const box = el('textarea', attrs) as HTMLTextAreaElement;
    box.value = existing?.[hook] ?? '';
    boxes.set(hook, box);

    const state = el('span', { class: 'muted small' });
    const button = el('button', { type: 'button', class: 'copy-action' }) as HTMLButtonElement;

    /** Say which of the two meanings is currently in force. */
    const sync = (): void => {
      const custom = box.value.trim() !== '';
      button.textContent = custom ? 'Reset to default' : 'Edit this wording';
      // needs_min is shown on the event, never sent anywhere.
      const verb = hook === 'needs_min' ? 'Showing' : 'Sending';
      state.textContent = custom ? `${verb} your wording.` : `${verb} the default shown above.`;
    };

    button.addEventListener('click', () => {
      // The default carries {placeholders} and they must survive into the box:
      // the organizer is editing a template, not a rendered message. So this is
      // the raw registry string, which is exactly what the placeholder shows.
      box.value = box.value.trim() !== '' ? '' : fallback;
      sync();
      box.focus();
      // Caret at the end, or editing starts by overwriting what was just filled in.
      box.setSelectionRange(box.value.length, box.value.length);
    });

    // `input`, not `change`: typing into an empty box IS the switch from default
    // to custom, and waiting for blur to admit it looks like a dead control.
    box.addEventListener('input', sync);
    sync();

    // Wrapped, so the label, the box and its action row hide as ONE unit. Hiding
    // only the textarea would leave an orphan label above an orphan button.
    const field = el('div', { class: 'copy-field' }, [
      el('label', { for: id }, hookLabel(hook)),
      box,
      el('div', { class: 'copy-actions' }, [button, state]),
    ]);
    fields.set(hook, field);
    children.push(field);
  }

  // Shown only while something is hidden. Without it the block silently has
  // fewer boxes than the type actually supports, and the connection to the
  // minimum and maximum above is guesswork.
  const note = el(
    'p',
    { class: 'muted small', hidden: true },
    'Set a minimum or a maximum above to add its message.',
  );
  children.push(note);

  return {
    element: el('div', { class: 'message-copy' }, children),
    value(): Record<string, string> {
      // Deliberately NOT filtered by visibility. A hidden box holds wording the
      // organizer typed while the threshold was set, and dropping it here would
      // delete their text the moment they cleared the minimum.
      const out: Record<string, string> = {};
      for (const [hook, box] of boxes) {
        const v = box.value.trim();
        if (v !== '') out[hook] = v;
      }
      return out;
    },
    setReachable(canFire): void {
      let anyHidden = false;
      for (const [hook, field] of fields) {
        const show = canFire(hook);
        field.hidden = !show;
        if (!show) anyHidden = true;
      }
      note.hidden = !anyHidden;
    },
  };
}
